import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import QuickCaptureBtn from "./QuickCaptureBtn";
import Browse from "./GermanNounArticles/Browse";
import ReportProblem from "./GermanNounArticles/ReportProblem";
import RoundResults from "./GermanNounArticles/RoundResults";
import { burstPieces, pickEmoji, pickHeadline, shouldBurst } from "./GermanNounArticles/celebration";
import Settings, { DEFAULT_COLORS, GENDER_NAMES } from "./GermanNounArticles/Settings";
import SignUpPrompt from "./GermanNounArticles/SignUpPrompt";
import useLabSync from "./GermanNounArticles/useLabSync";
import useLocalStorage, { readStored, writeStored } from "./GermanNounArticles/useLocalStorage";
import { audioUrl } from "./GermanNounArticles/audio";
import { bandCount, bandIndices, buildQueue } from "./GermanNounArticles/deck";
import { DEFAULT_ROUND_SIZE, THEMES } from "./GermanNounArticles/options";

// Show a German noun, pick its article: der, die or das.
//
// The deck arrives ordered by frequency, which is the point of it - that order
// drives the difficulty bands in Browse, so a learner can skip the words they
// already know and drill the band they don't.
//
// Three answers rather than two, and no elision - otherwise the same shape as
// the French noun gender lab.
//
// Known words and settings persist in localStorage; there is no backend here.

// Signing in unions the account's known words into whatever this browser has,
// so practising anonymously and then signing up never loses anything. Settings
// are last-write-wins, and the account is the later writer by definition.
//
// Runs before the state hooks read localStorage - see the useState call below.
function mergeSavedState(savedState) {
  if (!savedState) return false;

  const saved = savedState.known || [];
  if (saved.length) {
    writeStored("known", Array.from(new Set([...readStored("known", []), ...saved])));
  }

  Object.entries(savedState.settings || {}).forEach(([key, value]) => writeStored(key, value));

  return true;
}

function bodyIsDark() {
  try {
    return document.body.classList.contains("dark-mode");
  } catch (e) {
    return false;
  }
}

// "auto" follows the OS. Where the OS expresses no preference - or the browser
// can't be asked - fall back to dark rather than assuming light.
function systemPrefersDark() {
  try {
    if (!window.matchMedia) return true;
    if (window.matchMedia("(prefers-color-scheme: dark)").matches) return true;
    if (window.matchMedia("(prefers-color-scheme: light)").matches) return false;

    return true;
  } catch (e) {
    return true;
  }
}

// A signed in user who has never touched this control should match the rest of
// Clozemaster, so adopt whatever the account rendered as an explicit choice.
// Anyone who has chosen here keeps their choice.
function seedThemeFromAccount(isSignedIn) {
  if (isSignedIn && readStored("theme", null) === null) {
    writeStored("theme", bodyIsDark() ? "dark" : "light");
  }

  return true;
}

// Long enough for the chime to land before the word starts.
const CHIME_MS = 320;

// A round needs an end. Without one it's "everything in the enabled bands",
// which can be 2,500 cards - no finish line, no completion, no moment to stop
// or to go again.
//
// Ten by default: the finish line is the point, and a shorter one arrives often
// enough to keep earning the "one more round" impulse. Re-asked misses make a
// round longer than its nominal length anyway.


// Both right and wrong advance on their own - having only one of them do it
// meant forgetting which, and sitting there waiting. A miss gets longer,
// because there's a rule and a translation to read. The countdown bar animates
// over exactly these, so change both or neither.
const AUTO_ADVANCE_MS = 2400;
const AUTO_ADVANCE_MISS_MS = 4800;

// A missed word comes back later in the same round. Far enough away that you
// recall it rather than parrot it, close enough to still be the same session.
const RETRY_GAP = 4;

const QUICK_CAPTURE_REASONS = {
  disabled: "Quick Capture is currently turned off",
  no_pairing: "No German course found to capture this word into",
  unavailable: "Quick Capture isn't available here",
};

export default function GermanNounArticles({
  audioBaseUrl,
  correctAudioUrl,
  gameSettingsUrl,
  isSignedIn,
  nouns,
  quickCapture,
  quickCaptureReason,
  reportUrl,
  savedState,
  signInUrl,
  signUpUrl,
  stateUrl,
}) {
  // Deliberately first: hooks run top to bottom, so this lands the account's
  // state in localStorage before the hooks below read it, and the first render
  // is already built from the merged set.
  useState(() => mergeSavedState(savedState));
  useState(() => seedThemeFromAccount(isSignedIn));

  const sync = useLabSync(stateUrl);
  const totalBands = bandCount(nouns.length);
  const allBands = Array.from({ length: totalBands }, (_, i) => i);

  const [known, setKnown] = useLocalStorage("known", []);
  const [storedBands, setStoredBands] = useLocalStorage("bands", allBands);
  const [shuffle, setShuffle] = useLocalStorage("shuffle", true);
  const [colorCoding, setColorCoding] = useLocalStorage("colorCoding", false);
  const [autoplayAudio, setAutoplayAudio] = useLocalStorage("autoplayAudio", true);
  const [loopAudio, setLoopAudio] = useLocalStorage("loopAudio", false);
  const [background, setBackground] = useLocalStorage("background", true);
  const [soundEffects, setSoundEffects] = useLocalStorage("soundEffects", true);
  const [autoAdvance, setAutoAdvance] = useLocalStorage("autoAdvance", true);
  const [roundSize, setRoundSize] = useLocalStorage("roundSize", DEFAULT_ROUND_SIZE);
  const [theme, setTheme] = useLocalStorage("theme", "auto");
  const [systemDark, setSystemDark] = useState(systemPrefersDark);
  const darkModeOn = theme === "dark" || (theme === "auto" && systemDark);
  const [colors, setColors] = useLocalStorage("colors", DEFAULT_COLORS);
  const [signUpDismissed, setSignUpDismissed] = useLocalStorage("signUpDismissed", false);

  // A stored band list can outlive the deck it was saved against - regenerating
  // with a different LIMIT changes how many bands exist.
  const bands = storedBands.filter((band) => band < totalBands);

  const [view, setView] = useState("practice");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [queue, setQueue] = useState(() => buildQueue({ nouns, known, bands, shuffle }));
  const [roundStart, setRoundStart] = useState(0);
  const [roundQueue, setRoundQueue] = useState(() => queue.slice(0, roundSize));
  const [position, setPosition] = useState(0);
  const [answer, setAnswer] = useState(null);
  const [score, setScore] = useState({ correct: 0, answered: 0 });
  const [results, setResults] = useState([]);

  const noun = nouns[roundQueue[position]];
  const done = position >= roundQueue.length;

  useEffect(() => {
    sync.push({
      known,
      settings: {
        autoAdvance,
        roundSize,
        autoplayAudio,
        background,
        soundEffects,
        bands: storedBands,
        colorCoding,
        colors,
        loopAudio,
        shuffle,
        signUpDismissed,
      },
    });
  }, [
    autoAdvance,
    roundSize,
    autoplayAudio,
    background,
    soundEffects,
    colorCoding,
    colors,
    known,
    loopAudio,
    shuffle,
    signUpDismissed,
    storedBands,
    sync,
  ]);

  // The whole page is inside body, including the Clozemaster chrome, so this is
  // the class the rest of the app already styles against.
  useEffect(() => {
    document.body.classList.toggle("dark-mode", darkModeOn);
  }, [darkModeOn]);

  // On "auto", follow the OS while the page is open rather than only at load.
  useEffect(() => {
    if (!window.matchMedia) return undefined;

    const query = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => setSystemDark(systemPrefersDark());

    if (query.addEventListener) query.addEventListener("change", onChange);
    else if (query.addListener) query.addListener(onChange);

    return () => {
      if (query.removeEventListener) query.removeEventListener("change", onChange);
      else if (query.removeListener) query.removeListener(onChange);
    };
  }, []);

  // Mirrors the dashboard's v5-bg-image-disabled: the image lives on the body,
  // so turning it off is a class rather than a re-render.
  useEffect(() => {
    document.body.classList.toggle("labs-bg-disabled", !background);
  }, [background]);

  // Writes through to the pairing's game_settings via the same endpoint the
  // dashboard toggle posts to - it propagates the theme across all of the
  // user's pairings, so this isn't a setting that only applies to this page.
  const chooseTheme = (choice) => {
    setTheme(choice);

    const dark = choice === "dark" || (choice === "auto" && systemDark);
    if (!gameSettingsUrl) return;

    const token = document.querySelector('meta[name="csrf-token"]');

    fetch(gameSettingsUrl, {
      body: JSON.stringify({ settings: { theme: dark ? "dark" : "light" } }),
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": token ? token.getAttribute("content") : "",
      },
      method: "POST",
    }).catch(() => {
      // The page is already in the right theme; a failed write just means it
      // won't survive to the next page load.
    });
  };

  // The audio is the article plus the noun, so it can only play once the answer
  // is in - which also means there has always been a click first, and autoplay
  // is never blocked.
  const audioEl = useRef(null);
  const [audioPlaying, setAudioPlaying] = useState(false);
  // Cancelled for this card only - the setting is untouched.
  const [stayOnWord, setStayOnWord] = useState(false);
  const playAudio = useCallback(() => {
    const el = audioEl.current;
    if (!el) return;

    el.currentTime = 0;
    // A missing file or a browser that declines is not worth interrupting
    // practice over.
    el.play().catch(() => {});
  }, []);

  useEffect(() => {
    // Advancing unmounts the audio, so nothing is playing on the next word.
    if (!answer) {
      setAudioPlaying(false);
      setStayOnWord(false);
      return undefined;
    }

    // Only on a correct answer - a wrong guess is the normal case here, and
    // making a noise about it would just be punishing.
    const chimed = soundEffects && correctAudioUrl && answer === noun.article;
    if (chimed) {
      const chime = new Audio(correctAudioUrl);
      chime.play().catch(() => {});
    }

    if (!autoplayAudio) return undefined;

    // Starting the word on top of the chime is muddy, so let the chime land
    // first when there was one.
    if (!chimed) {
      playAudio();
      return undefined;
    }

    const timer = setTimeout(playAudio, CHIME_MS);
    return () => clearTimeout(timer);
  }, [answer, autoplayAudio, correctAudioUrl, noun, playAudio, soundEffects]);

  // A looping clip otherwise plays until you leave the word, and the button
  // would only ever restart it. Off loop the clip is a second long, so replay
  // stays the more useful action.
  const stoppable = loopAudio && audioPlaying;
  const toggleAudio = () => {
    setStayOnWord(true);

    if (!stoppable) {
      playAudio();
      return;
    }

    if (audioEl.current) audioEl.current.pause();
  };

  // Anything that changes *which* nouns are in play starts a fresh round -
  // carrying a score across a deck change would be averaging two different things.
  const startRound = useCallback(
    (overrides = {}) => {
      const { roundSize: size = roundSize, ...rest } = overrides;
      const rebuilt = buildQueue({ nouns, bands, known, shuffle, ...rest });

      setQueue(rebuilt);
      // Taken from the overrides when given: a setState isn't visible to the
      // caller that triggered it, so a new length has to be passed in.
      setRoundQueue(rebuilt.slice(0, size));
      setRoundStart(0);
      setPosition(0);
      setAnswer(null);
      setScore({ correct: 0, answered: 0 });
      setResults([]);
    },
    [bands, known, nouns, roundSize, shuffle]
  );

  const answerWith = useCallback(
    (article) => {
      if (answer || !noun) return;

      const right = article === noun.article;
      const firstAttempt = !results.some((r) => r.noun.word === noun.word);

      setAnswer(article);

      // Only the first attempt counts. The round makes you get a missed word
      // right before it ends; the score still says what you knew.
      if (firstAttempt) {
        setResults((previous) => [...previous, { noun, right }]);
        setScore((previous) => ({
          correct: previous.correct + (right ? 1 : 0),
          answered: previous.answered + 1,
        }));
      }
    },
    [answer, noun, results]
  );

  const keepPracticing = useCallback(() => {
    if (!answer) return;

    // Put a missed word back into the round a few cards along.
    if (noun && answer !== noun.article) {
      setRoundQueue((previous) => {
        const next = previous.slice();
        next.splice(Math.min(position + RETRY_GAP, next.length), 0, previous[position]);

        return next;
      });
    }

    setAnswer(null);
    setPosition((previous) => previous + 1);
  }, [answer, noun, position]);

  // Marking known pulls the card out of the current round rather than rebuilding
  // it, so you don't lose your place or your score mid-round.
  const markKnown = useCallback(() => {
    if (!noun) return;

    const deckIndex = roundQueue[position];

    setKnown((previous) => (previous.includes(noun.word) ? previous : [...previous, noun.word]));
    setRoundQueue((previous) => previous.filter((_, i) => i !== position));
    setQueue((previous) => previous.filter((index) => index !== deckIndex));
    setAnswer(null);
  }, [noun, position, roundQueue, setKnown]);

  const nextRound = () => {
    const knownWords = new Set(known);
    const remaining = queue.filter((index) => !knownWords.has(nouns[index].word));

    // Walk on past whatever of this round still survives in the queue. Counting
    // roundSize would skip unplayed nouns, because marking a word known - in
    // the round or from the results list - has already removed it from queue
    // ahead of us.
    const played = new Set(roundQueue);
    const consumed = remaining.slice(roundStart).findIndex((index) => !played.has(index));
    const next = roundStart + (consumed === -1 ? remaining.length - roundStart : consumed);
    const start = next >= remaining.length || next < 0 ? 0 : next;

    setQueue(remaining);
    setRoundQueue(remaining.slice(start, start + roundSize));
    setRoundStart(start);
    setPosition(0);
    setAnswer(null);
    setScore({ correct: 0, answered: 0 });
    // Stale results carry marks into the next round's summary, and make
    // firstAttempt treat an already-seen word as a repeat so it never scores.
    setResults([]);
  };

  // Being right shouldn't cost a click. Being wrong still waits, because that's
  // the card you need to read.
  const advancing = Boolean(autoAdvance && answer && noun && !stayOnWord);
  const advanceMs = answer && noun && answer === noun.article
    ? AUTO_ADVANCE_MS
    : AUTO_ADVANCE_MISS_MS;

  useEffect(() => {
    if (!advancing) return undefined;

    const timer = setTimeout(keepPracticing, advanceMs);
    return () => clearTimeout(timer);
  }, [advanceMs, advancing, keepPracticing]);

  useEffect(() => {
    if (view !== "practice") return undefined;

    const onKeyDown = (event) => {
      if (event.target.tagName === "INPUT" || event.target.tagName === "TEXTAREA") return;

      // Three answers don't map onto a left/right pair, so number them.
      if (event.key === "1") answerWith("der");
      else if (event.key === "2") answerWith("die");
      else if (event.key === "3") answerWith("das");
      else if (event.key === "k" || event.key === "K") markKnown();
      else if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        keepPracticing();
      } else return;
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [answerWith, keepPracticing, markKnown, view]);

  // Rolled once when the round ends, so it doesn't reshuffle on a re-render.
  // Above the early returns below: a hook that only runs in some views changes
  // the hook count and React refuses to render.
  const celebration = useMemo(
    () => {
      const pct = score.answered ? Math.round((score.correct / score.answered) * 100) : 0;

      return {
        emoji: pickEmoji(),
        headline: pickHeadline(pct, score.answered),
        pieces: burstPieces(pct),
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [done, roundStart]
  );

  const toggleBand = (band) => {
    const next = bands.includes(band) ? bands.filter((b) => b !== band) : [...bands, band].sort((a, b) => a - b);

    setStoredBands(next);
    startRound({ bands: next });
  };

  const setBandKnown = (band, shouldKnow) => {
    const words = bandIndices(band, nouns.length).map((i) => nouns[i].word);
    const inBand = new Set(words);
    const next = shouldKnow
      ? Array.from(new Set([...known, ...words]))
      : known.filter((word) => !inBand.has(word));

    if (!shouldKnow) sync.markRemoved(known.filter((word) => inBand.has(word)));
    setKnown(next);
    startRound({ known: next });
  };

  // From the results list: record it, but leave the round on screen. nextRound
  // drops it from the queue.
  const toggleKnownQuietly = (word) => {
    const removing = known.includes(word);

    if (removing) sync.markRemoved([word]);
    setKnown(removing ? known.filter((w) => w !== word) : [...known, word]);
  };

  const toggleKnown = (word) => {
    const removing = known.includes(word);
    const next = removing ? known.filter((w) => w !== word) : [...known, word];

    if (removing) sync.markRemoved([word]);
    setKnown(next);
    startRound({ known: next });
  };

  const settings = (
    <Settings
      audioAvailable={Boolean(audioBaseUrl)}
      background={background}
      autoAdvance={autoAdvance}
      roundSize={roundSize}
      soundEffects={soundEffects}
      autoplayAudio={autoplayAudio}
      colorCoding={colorCoding}
      colors={colors}
      systemDark={systemDark}
      theme={theme}
      loopAudio={loopAudio}
      onSetColor={(gender, value) => setColors({ ...colors, [gender]: value })}
      onSetRoundSize={(size) => {
        setRoundSize(size);
        startRound({ roundSize: size });
      }}
      onToggleAutoAdvance={() => setAutoAdvance(!autoAdvance)}
      onToggleAutoplayAudio={() => setAutoplayAudio(!autoplayAudio)}
      onToggleBackground={() => setBackground(!background)}
      onToggleSoundEffects={() => setSoundEffects(!soundEffects)}
      onToggleColorCoding={() => setColorCoding(!colorCoding)}
      onSetTheme={chooseTheme}
      onToggleLoopAudio={() => setLoopAudio(!loopAudio)}
      onToggleShuffle={() => {
        setShuffle(!shuffle);
        startRound({ shuffle: !shuffle });
      }}
      shuffle={shuffle}
    />
  );

  const settingsPanel = (
    <div className="gna-settings-wrap">
      <button
        aria-expanded={settingsOpen}
        className="gna-settings-toggle"
        onClick={() => setSettingsOpen(!settingsOpen)}
        type="button"
      >
        <span className="glyphicon glyphicon-cog"></span> Settings
      </button>
      {settingsOpen && settings}
    </div>
  );

  const signUpBar =
    !isSignedIn && !signUpDismissed ? (
      <SignUpPrompt
        onDismiss={() => setSignUpDismissed(true)}
        signInUrl={signInUrl}
        signUpUrl={signUpUrl}
      />
    ) : null;

  const tabs = (
    <div className="gna-tabs">
      {[["practice", "Practice"], ["browse", "Browse"]].map(([name, label]) => (
        <button
          className={`gna-tab${view === name ? " gna-tab-active" : ""}`}
          key={name}
          onClick={() => setView(name)}
          type="button"
        >
          {label}
        </button>
      ))}
    </div>
  );

  if (view === "browse") {
    return (
      <div className="fna">
        {tabs}
        {signUpBar}
        <Browse
          bands={bands}
          colorCoding={colorCoding}
          colors={colors}
          known={known}
          nouns={nouns}
          onSetBandKnown={setBandKnown}
          onToggleBand={toggleBand}
          onToggleKnown={toggleKnown}
        />
        {settingsPanel}
      </div>
    );
  }

  const accuracy = score.answered ? Math.round((score.correct / score.answered) * 100) : 0;


  if (queue.length === 0 && results.length === 0) {
    return (
      <div className="fna">
        {tabs}
        {signUpBar}
        <div className="gna-empty">
          <p>Nothing left to practice.</p>
          <p className="gna-hint">
            Every noun in the bands you picked is marked as known. Turn on another
            band or bring some words back under Browse.
          </p>
        </div>
        {settingsPanel}
      </div>
    );
  }

  if (done) {
    return (
      <div className="fna">
        {tabs}
        <div className="gna-done">
          <p className="gna-done-kicker">Round complete</p>

          {/* One mark per card, in the order you met them - compact, and the
              shape of the round at a glance. */}
          {results.length > 0 && (
            <div className="gna-done-marks">
              {results.map((r, i) => (
                <span
                  className={`gna-done-mark${r.right ? "" : " gna-done-mark-missed"}`}
                  // eslint-disable-next-line react/no-array-index-key
                  key={i}
                  title={r.noun.written}
                />
              ))}
            </div>
          )}

          <p className="gna-done-score">
            {score.correct} <span>/ {score.answered}</span>
          </p>
          <h2 className="gna-done-headline">{celebration.headline}</h2>

          {shouldBurst(accuracy, score.answered) && (
            <div aria-hidden="true" className="gna-burst">
              {celebration.pieces.map((piece) => (
                <span
                  className="gna-burst-piece"
                  key={piece.key}
                  style={{
                    animationDelay: `${piece.delay}ms`,
                    animationDuration: `${piece.duration}ms`,
                    fontSize: `${piece.size}px`,
                    "--drift": `${piece.drift}px`,
                    "--fall": `${piece.fall}px`,
                    "--rise": `${piece.rise}px`,
                    "--spin": `${piece.spin}deg`,
                  }}
                >
                  {celebration.emoji}
                </span>
              ))}
            </div>
          )}

          {results.length > 0 && (
            <RoundResults
              audioBaseUrl={audioBaseUrl}
              colorCoding={colorCoding}
              colors={colors}
              known={known}
              onToggleKnown={toggleKnownQuietly}
              quickCapture={quickCapture}
              results={results}
            />
          )}
          <p className={`gna-done-progress`}>
            {Math.min(roundStart + roundQueue.length, queue.length)} of {queue.length} in
            {" "}
            {known.length > 0 ? "what's left of " : ""}
            your deck
          </p>
          <button className={`btn btn-success gna-done-next`} onClick={nextRound} type="button">
            Next Round
          </button>
          {!isSignedIn && (
            <SignUpPrompt signInUrl={signInUrl} signUpUrl={signUpUrl} variant="card" />
          )}
        </div>
        {settingsPanel}
      </div>
    );
  }

  const correct = answer === noun.article;

  return (
    <div className="fna">
      {tabs}
      {signUpBar}

      <div className="gna-status">
        <span>
          {position + 1} / {roundQueue.length}
        </span>
        <span>
          {score.correct} / {score.answered} correct
          {score.answered > 0 && ` (${accuracy}%)`}
        </span>
      </div>

      <div
        className="gna-word"
        style={colorCoding && answer ? { color: colors[noun.gender] } : null}
      >
        {answer ? (
          noun.written
        ) : (
          <>
            {/* Reads the prompt as a fill-in-the-blank rather than a bare word. */}
            <span className="gna-blank" />
            {noun.word}
          </>
        )}

        {/* The audio is the article and noun together, so the control belongs
            beside them - next to the gender it read as playing "masculine". */}
        {answer && audioBaseUrl && (
          <>
            <button
              aria-label={`${stoppable ? "Stop" : "Play"} ${noun.written}`}
              className={`gna-audio-btn${stoppable ? " gna-audio-btn-playing" : ""}`}
              onClick={toggleAudio}
              type="button"
            >
              <span className="glyphicon glyphicon-volume-up"></span>
            </button>
            <audio
              key={`audio-${noun.word}`}
              loop={loopAudio}
              onEnded={() => setAudioPlaying(false)}
              onPause={() => setAudioPlaying(false)}
              onPlay={() => setAudioPlaying(true)}
              preload="auto"
              ref={audioEl}
              src={audioUrl(audioBaseUrl, noun.written)}
            />
          </>
        )}
      </div>

      <div className="gna-choices">
        {["der", "die", "das"].map((article) => {
          // Once answered, mark the right answer green and a wrong pick red.
          let state = "";
          if (answer) {
            if (article === noun.article) state = " gna-choice-correct";
            else if (article === answer) state = " gna-choice-incorrect";
          }

          return (
            <button
              className={`gna-choice${state}`}
              disabled={Boolean(answer)}
              key={article}
              onClick={() => answerWith(article)}
              type="button"
            >
              {article}
              {state && (
                <span
                  className={`glyphicon glyphicon-${
                    article === noun.article ? "ok" : "remove"
                  } gna-choice-icon`}
                />
              )}
            </button>
          );
        })}
      </div>

      {answer && (
        <div
          aria-live="polite"
          className={`gna-feedback gna-feedback-${correct ? "correct" : "incorrect"}`}
          onClick={() => setStayOnWord(true)}
          role="status"
        >
          {/* Colour alone can't carry right-or-wrong - say it, and show a mark. */}
          <div className="gna-feedback-verdict">
            <span className={`glyphicon glyphicon-${correct ? "ok" : "remove"}`} />
            {correct ? "Correct" : "Not quite"}
          </div>

          <div className="gna-feedback-gender">{GENDER_NAMES[noun.gender]}</div>

          {/* Why, not just what - the rule is what makes the next word of the
              same shape guessable. Attached at deck-build time. */}
          {noun.rule && (
            <div className="gna-feedback-rule">
              {noun.rule}
              {noun.ruleException && <strong> — this one is an exception</strong>}
            </div>
          )}

          {noun.translations.length > 0 && (
            <div className="gna-feedback-translations">{noun.translations.join("; ")}</div>
          )}

          <div className="gna-feedback-meta">
            {/* The glosses are Wiktionary's, reproduced verbatim - CC BY-SA
                attribution, and a way into the full entry for anyone who wants
                more than three translations. */}
            <a
              href={`https://en.wiktionary.org/wiki/${encodeURIComponent(noun.word)}#German`}
              rel="noopener noreferrer"
              target="_blank"
            >
              Wiktionary
            </a>
            <ReportProblem key={noun.word} reportUrl={reportUrl} word={noun.word} />
          </div>
        </div>
      )}

      {/* Says how long you have, and takes it back if you want longer. The
          setting stays on; this only stops the current card. */}
      {advancing && (
        <button
          aria-label="Stay on this word"
          className="gna-advancing"
          onClick={() => setStayOnWord(true)}
          title="Stay on this word"
          type="button"
        >
          <span className="gna-advancing-track">
            <span
              className="gna-advancing-fill"
              style={{ animationDuration: `${advanceMs}ms` }}
            />
          </span>
        </button>
      )}

      <div className="gna-actions">
        <button
          className="btn btn-success"
          disabled={!answer}
          onClick={keepPracticing}
          type="button"
        >
          Keep Practicing
        </button>

        <button className="btn btn-default" onClick={markKnown} type="button">
          Known
        </button>

        {quickCapture ? (
          <QuickCaptureBtn
            autoSubmitInitialText
            className="btn btn-default"
            collectionsUrl={quickCapture.collectionsUrl}
            demoEntries={quickCapture.demoEntries}
            entriesUrl={quickCapture.entriesUrl}
            importsUrl={quickCapture.importsUrl}
            initialText={noun.word}
            isPro={quickCapture.isPro}
            onOpen={() => setStayOnWord(true)}
            // Remount per noun, so reopening the modal starts from the word on
            // screen rather than the one it was first opened with.
            key={noun.word}
            targetLanguageEnglishName={quickCapture.targetLanguageEnglishName}
            targetLanguageIso={quickCapture.targetLanguageIso}
            targetLanguageName={quickCapture.targetLanguageName}
            text=" See in context"
          />
        ) : (
          <button
            className="btn btn-default"
            disabled
            title={QUICK_CAPTURE_REASONS[quickCaptureReason] || QUICK_CAPTURE_REASONS.unavailable}
            type="button"
          >
            <span className="glyphicon glyphicon-flash"></span> See in context
          </button>
        )}
      </div>

      <div className="gna-hint">
        1 der · 2 die · 3 das · enter to continue · k to mark known
      </div>

      {settingsPanel}
    </div>
  );
}

GermanNounArticles.defaultProps = {
  audioBaseUrl: null,
  correctAudioUrl: null,
  gameSettingsUrl: null,
  isSignedIn: false,
  quickCapture: null,
  quickCaptureReason: null,
  savedState: null,
  stateUrl: null,
};

GermanNounArticles.propTypes = {
  audioBaseUrl: PropTypes.string,
  correctAudioUrl: PropTypes.string,
  gameSettingsUrl: PropTypes.string,
  isSignedIn: PropTypes.bool,
  nouns: PropTypes.arrayOf(
    PropTypes.shape({
      word: PropTypes.string.isRequired,
      gender: PropTypes.oneOf(["m", "f", "n"]).isRequired,
      article: PropTypes.oneOf(["der", "die", "das"]).isRequired,
      written: PropTypes.string.isRequired,
      rule: PropTypes.string,
      ruleException: PropTypes.bool,
      translations: PropTypes.arrayOf(PropTypes.string).isRequired,
    })
  ).isRequired,
  quickCapture: PropTypes.shape({
    collectionsUrl: PropTypes.string,
    demoEntries: PropTypes.array,
    entriesUrl: PropTypes.string,
    importsUrl: PropTypes.string,
    isPro: PropTypes.bool,
    targetLanguageEnglishName: PropTypes.string,
    targetLanguageIso: PropTypes.string,
    targetLanguageName: PropTypes.string,
  }),
  quickCaptureReason: PropTypes.oneOf(Object.keys(QUICK_CAPTURE_REASONS)),
  reportUrl: PropTypes.string.isRequired,
  savedState: PropTypes.shape({
    known: PropTypes.arrayOf(PropTypes.string),
    settings: PropTypes.object,
  }),
  signInUrl: PropTypes.string.isRequired,
  signUpUrl: PropTypes.string.isRequired,
  stateUrl: PropTypes.string,
};
